import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";

export default function SecurityPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = String(email || "").trim().toLowerCase();
    if (!cleaned) {
      toast.error("Enter your email address");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: cleaned }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Request failed (${res.status})`);
      setSent(true);
      toast.success("Check your email", { description: "If an account exists, we sent a reset link." });
    } catch (err) {
      toast.error("Could not send reset link", { description: err?.message || "Try again later." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen p-8 lg:p-12" style={{ background: "var(--bg-base)", color: "#FAFAFA" }}>
      <div className="max-w-[480px] mx-auto">
        <div className="mb-10"><Logo /></div>
        <div className="flex items-center gap-2 text-overline text-[var(--primary)]">
          <ShieldCheck size={14} /> Account security
        </div>
        <h1 className="font-heading text-4xl mt-2 tracking-tight">Reset password</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-3">
          Enter the <strong className="text-[var(--text-primary)]">same work email you use to sign in</strong> (your AivoryEMS operator address).
          The reset link is sent from <strong className="text-[var(--text-primary)]">support@aivoryems.com</strong> — check spam and promotions if it does not arrive within a few minutes.
        </p>

        {sent ? (
          <div className="surface mt-8 p-6">
            <p className="text-sm text-[var(--text-secondary)]">
              If <strong className="text-[var(--text-primary)]">{email}</strong> matches an operator account on this workspace, you will receive a reset email shortly. If nothing arrives, confirm the address is correct or contact your workspace admin — personal emails that are not on the team roster cannot be reset here.
            </p>
            <button type="button" className="btn-secondary w-full justify-center mt-6" onClick={() => setSent(false)}>
              Try another email
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-password-form">
            <div>
              <label className="text-overline">Email</label>
              <input
                type="email"
                className="input-dark mt-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                data-testid="forgot-email-input"
              />
            </div>
            <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid="forgot-submit">
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}

        <div className="surface mt-8 p-5 text-xs text-[var(--text-tertiary)] leading-relaxed">
          AivoryEMS uses encrypted transport, role-based access, and audit logging for operator actions.
        </div>

        <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--primary)]">
          <ArrowLeft size={14} /> Back to login
        </Link>
      </div>
    </div>
  );
}
